/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped resource: responses are passed through as the server sends them. */
import { SdkContext } from '../internal'
import type { CourseContinueItem, CourseListItem, CourseReaction } from '@/types/courses'

export class CoursesResource {
  constructor(private ctx: SdkContext) {}

  /**
   * One page of the courses of a space. The server pages at 10 by default.
   * Each item carries `product` with the caller's `access` and `canEnter` (F-191 phase 3).
   */
  async list(params: { spaceId?: string; page?: number; limit?: number; saved?: boolean } = {}): Promise<{ items: CourseListItem[]; pagination?: { page: number; totalPages: number; total: number } }> {
    const result = await this.ctx.raw<any>({ httpMethod: 'GET', path: '/courses' }, { query: params })
    if (Array.isArray(result)) return { items: result as CourseListItem[] }
    return { items: result?.items || [], pagination: result?.pagination }
  }

  /** F-191: the caller's courses in progress, most recent first, each with the lesson to resume. */
  async continue(params: { limit?: number } = {}): Promise<CourseContinueItem[]> {
    const result = await this.ctx.raw<CourseContinueItem[] | { items?: CourseContinueItem[]; data?: CourseContinueItem[] }>({ httpMethod: 'GET', path: '/courses/continue' }, { query: params })
    if (Array.isArray(result)) return result
    return result?.items || result?.data || []
  }

  /** By slug or id. Lesson `body` and `contentUrl` come only when the viewer may read them. */
  async get(slugOrId: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: `/courses/{id}` }, { id: slugOrId })
  }

  async create(course: any) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses', hasBody: true }, { body: course })
  }

  async update(id: string, course: any) {
    return this.ctx.call({ httpMethod: 'PUT', path: `/courses/{id}`, hasBody: true }, { id, body: course })
  }

  async delete(id: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/courses/{id}` }, { id })
  }

  async createModule(module: any) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/module', hasBody: true }, { body: module })
  }

  async createLesson(lesson: any) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/lesson', hasBody: true }, { body: lesson })
  }

  async updateModule(moduleId: string, data: { title?: string; availableAt?: string | null; availableAfterDays?: number | null }) {
    return this.ctx.call({ httpMethod: 'PUT', path: '/courses/module/{moduleId}', hasBody: true }, { moduleId, body: data })
  }

  async deleteModule(moduleId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/module/{moduleId}' }, { moduleId })
  }

  /** Also moves the lesson to another module of the same course (`moduleId`). */
  async updateLesson(lessonId: string, data: any) {
    return this.ctx.call({ httpMethod: 'PUT', path: '/courses/lesson/{lessonId}', hasBody: true }, { lessonId, body: data })
  }

  async deleteLesson(lessonId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/lesson/{lessonId}' }, { lessonId })
  }

  /** `moduleIds` must be exactly the modules that exist, in the new order. */
  async reorderModules(courseId: string, moduleIds: string[]) {
    return this.ctx.call({ httpMethod: 'PUT', path: '/courses/{courseId}/modules/order', hasBody: true }, { courseId, body: { moduleIds } })
  }

  /** `lessonIds` must be exactly the lessons of the module, in the new order. */
  async reorderLessons(moduleId: string, lessonIds: string[]) {
    return this.ctx.call({ httpMethod: 'PUT', path: '/courses/module/{moduleId}/lessons/order', hasBody: true }, { moduleId, body: { lessonIds } })
  }

  /** The students of a course (staff and moderators), newest enrollment first. */
  async enrollments(courseId: string, params: { page?: number; limit?: number } = {}): Promise<{ items: any[]; pagination?: { page: number; limit: number; totalCount: number; totalPages: number } }> {
    const result = await this.ctx.raw<any>({ httpMethod: 'GET', path: '/courses/{id}/enrollments' }, { id: courseId, query: params })
    if (Array.isArray(result)) return { items: result }
    return { items: result?.items || [], pagination: result?.pagination }
  }

  async enroll(courseId: string) {
    return this.ctx.call({ httpMethod: 'POST', path: `/courses/{courseId}/enroll` }, { courseId })
  }

  /**
   * `{ completed: true }` concludes the lesson; `{ completed: false }` or `{}` only records
   * the access and never un-completes it.
   */
  async updateLessonProgress(courseId: string, lessonId: string, progress: { completed?: boolean; progressPercent?: number; source?: 'manual' | 'auto' } = {}) {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/courses/{courseId}/lesson/{lessonId}/progress`, hasBody: true },
      { courseId, lessonId, body: progress }
    )
  }

  /** Where the student stopped in the lesson video (seconds), read back as `resumeAt` on the lesson. */
  async saveLessonPosition(lessonId: string, positionSeconds: number, durationSeconds?: number) {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/courses/lesson/{lessonId}/progress', hasBody: true },
      { lessonId, body: { positionSeconds, ...(durationSeconds ? { durationSeconds } : {}) } }
    ) as Promise<{ lessonId: string; saved: boolean; ignored: 'throttled' | 'completed' | null; resumeAt: number | null }>
  }

  /** The student's 1-5 rating of a lesson. `comment` absent keeps it; `null` clears it. 201 first, 200 on edit. */
  async rateLesson(lessonId: string, data: { rating: number; comment?: string | null }) {
    return this.ctx.call({ httpMethod: 'PUT', path: '/courses/lesson/{lessonId}/rating', hasBody: true }, { lessonId, body: data })
  }

  /** Team: the course's and each lesson's average, count, distribution and comments count. */
  async courseRatings(courseId: string) {
    return this.ctx.call({ httpMethod: 'GET', path: '/courses/{courseId}/ratings' }, { courseId })
  }

  /** Team: one lesson's numbers and its comments (with the author), newest first. */
  async lessonRatings(lessonId: string, params: { page?: number; limit?: number } = {}): Promise<{ data: any; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    const body = await this.ctx.envelope<any>({ httpMethod: 'GET', path: '/courses/lesson/{lessonId}/ratings' }, { lessonId, query: params })
    return { data: body?.data, pagination: body?.pagination }
  }

  /** F-06 5. Step 1 of a hosted upload: a signed `PUT` URL bound to the exact type and size. Never log it. */
  async requestMediaUpload(lessonId: string, file: { name: string; mimeType: string; size: number }) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/lesson/{lessonId}/media/upload-url', hasBody: true }, { lessonId, body: file })
  }

  /** Step 3: attaches the uploaded object to the lesson; answers with the usage and `warning`. */
  async confirmMedia(lessonId: string, data: { key: string; name: string; size: number }) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/lesson/{lessonId}/media/confirm', hasBody: true }, { lessonId, body: data })
  }

  async removeMedia(lessonId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/lesson/{lessonId}/media' }, { lessonId })
  }

  /** A short-lived signed read URL (video 1 hour, file 5 minutes). Never store it. */
  async getMediaUrl(courseId: string, lessonId: string) {
    return this.ctx.call({ httpMethod: 'GET', path: '/courses/{courseId}/lesson/{lessonId}/media' }, { courseId, lessonId })
  }

  /** Grades a quiz. `answers`: one `{ questionId, optionId }` per question. 429 past 30 attempts an hour. */
  async submitQuizAttempt(courseId: string, lessonId: string, answers: Array<{ questionId: string; optionId: string }>) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/{courseId}/lesson/{lessonId}/quiz/attempts', hasBody: true }, { courseId, lessonId, body: { answers } })
  }

  /** The caller's last 10 attempts, with the community `timeZone`. */
  async listQuizAttempts(courseId: string, lessonId: string) {
    return this.ctx.call({ httpMethod: 'GET', path: '/courses/{courseId}/lesson/{lessonId}/quiz/attempts' }, { courseId, lessonId })
  }

  /** Leaves the course: the enrollment turns `cancelled` and the progress is kept. 409 once completed. */
  async leave(courseId: string) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/{courseId}/leave' }, { courseId })
  }

  /** Without `lessonId`, the course conversation; with it, the conversation of that lesson. */
  async listComments(courseId: string, params: { lessonId?: string; page?: number; limit?: number } = {}): Promise<{ items: any[]; pagination?: { page: number; limit: number; total: number; totalPages: number }; meta?: { canWrite: boolean } }> {
    // The envelope is read whole: `meta.canWrite` does not survive the unwrapping client (APP-96).
    const body = await this.ctx.envelope<any>({ httpMethod: 'GET', path: '/courses/{courseId}/comments' }, { courseId, query: params })
    return { items: body?.data || [], pagination: body?.pagination, meta: body?.meta }
  }

  async createComment(courseId: string, data: { content: string; lessonId?: string | null; parentId?: string | null }) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/{courseId}/comments', hasBody: true }, { courseId, body: data })
  }

  async updateComment(courseId: string, commentId: string, content: string) {
    return this.ctx.call({ httpMethod: 'PUT', path: '/courses/{courseId}/comments/{commentId}', hasBody: true }, { courseId, commentId, body: { content } })
  }

  async deleteComment(courseId: string, commentId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/{courseId}/comments/{commentId}' }, { courseId, commentId })
  }

  /** Adds or swaps the reaction. Returns `{ commentId, reactions }`. */
  async reactToComment(courseId: string, commentId: string, type: string) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/{courseId}/comments/{commentId}/react', hasBody: true }, { courseId, commentId, body: { type } })
  }

  /** Takes the session's reaction off. Returns `{ commentId, reactions }`. */
  async unreactToComment(courseId: string, commentId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/{courseId}/comments/{commentId}/react' }, { courseId, commentId })
  }

  /** Reactions to the course (no `lessonId`) or to a lesson. Returns `{ courseId, lessonId, reactions }`. */
  async getReactions(courseId: string, lessonId?: string) {
    return this.ctx.call<{ reactions?: CourseReaction[] }>({ httpMethod: 'GET', path: '/courses/{courseId}/reactions' }, { courseId, query: lessonId ? { lessonId } : {} })
  }

  async react(courseId: string, type: string, lessonId?: string | null) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/{courseId}/react', hasBody: true }, { courseId, body: lessonId ? { type, lessonId } : { type } })
  }

  async unreact(courseId: string, lessonId?: string | null) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/{courseId}/react' }, { courseId, query: lessonId ? { lessonId } : {} })
  }

  async favorite(courseId: string) {
    return this.ctx.call({ httpMethod: 'POST', path: '/courses/{courseId}/favorite' }, { courseId })
  }

  async unfavorite(courseId: string) {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/courses/{courseId}/favorite' }, { courseId })
  }

  /** 403 while the course has the ranking off. */
  async leaderboard(courseId: string) {
    return this.ctx.call({ httpMethod: 'GET', path: '/courses/{courseId}/leaderboard' }, { courseId })
  }
}
